<template>
  <teleport v-if="hasMounted" :to="selector">
    <component
      :is="wrapperTag"
      v-if="isWrapperOpen"
      ref="popper"
      v-resize="throttledResize"
      :class="wrapperClass"
      :[parentScopeId]="''"
      @popper-close.stop="close({ type: 'popper-close-event', event: $event })"
    >
      <transition :name="animation" appear @enter="enter" @before-leave="beforeLeave" @after-leave="afterLeave">
        <template v-if="!arrow">
          <component
            v-bind="$attrs"
            :is="tag"
            v-if="isOpen"
            :class="`${defaultClass} popper--${shadowStyle}-shadow ${noStyling && 'popper--empty-styling'}`"
            :[parentScopeId]="''"
          >
            <slot></slot>
          </component>
        </template>
        <template v-else>
          <component :is="arrowWrapperTag" v-if="isOpen" :class="arrowWrapperClass">
            <component
              v-bind="$attrs"
              :is="tag"
              :class="`${defaultClass} ${noStyling && 'popper--empty-styling'}`"
              :[parentScopeId]="''"
            >
              <slot></slot>
            </component>
            <slot name="arrow">
              <div data-popper-arrow class="popper-arrow-inner-wrapper">
                <div :class="arrowClass"></div>
              </div>
            </slot>
          </component>
        </template>
      </transition>
    </component>
  </teleport>
</template>

<script>
// The excessive element nesting is unfortunately required:
//
// A) Main wrapper needs to be there to avoid elements jumping around, which happens
// due to popper "disengaging" before the transition is done. This is also why we
// there're separate triggers for wrapper (isWrapperOpen) and the popper (isOpen).
// This is also best practice for popper.
//
// B) When we have arrows, we might do CSS tricks that puts part of the arrow
// underneath the popper. If we transition the arrow and popper separately with
// opactiy, then when we're half-way you'll see that the two elements overlap. To
// avoid this, we need to wrap them and transition the wrapper instead, since then
// no opacity (or anything else) is transitioned on the actual popper (and arrow).
//
// C) For the arrow (which is completely overrideable since it's slot default
// content), we wrap it to be able to use transform (since it's used by popper on
// the data-popper-arrow element).

import { getCurrentInstance } from 'vue';
import { insideVueDevtools } from '@lib/events';
import { createShortcutLayer, provideShortcutLayer } from '@lib/shortcuts';
import { createPopper } from '@popperjs/core';
import isElement from 'lodash/isElement';
import isFunction from 'lodash/isFunction';
import isNull from 'lodash/isNull';
import resizeDetection from '@lib/resize-detection';

function rafThrottle(callback) {
  let animationFrameId = null;
  let latestArgs;

  const throttled = function (...args) {
    latestArgs = args;
    if (animationFrameId !== null) return;

    animationFrameId = requestAnimationFrame(() => {
      animationFrameId = null;
      callback.apply(this, latestArgs);
    });
  };

  throttled.cancel = () => {
    if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  };

  return throttled;
}

// TODO: Rework API into a named parameters API where we separate event and $el into separate parameters
export function fsPopperClose($elOrEvent) {
  if (!$elOrEvent) {
    // Silently does nothing if no $elOrEvent is passed.
    // This usually happens if the popper was already closed and the caller forgot to check that the element is still in the DOM.
    return;
  }

  const $el = isElement($elOrEvent) ? $elOrEvent : $elOrEvent.currentTarget || $elOrEvent.target; // Fallback on .target to be able to handle bubbled component events (component events lack currentTarget since they're not proper DOM elements)

  $el.dispatchEvent(new Event('popper-close', { bubbles: true }));
}

export const FsPopper = {
  name: 'FsPopper',

  inheritAttrs: false,

  emits: ['close', 'enter', 'placement-changed', 'fully-closed', 'update:isOpen', 'open', 'early-close'],

  // The shortcut layer must be created here in setup, not in the transition's
  // enter() hook: slot content mounts before enter() runs, and its descendant
  // useShortcut()/usePrimaryAction() calls need the provide()d layer.
  setup(props, { emit }) {
    const instance = getCurrentInstance();
    const shortcutLayer = createShortcutLayer('Dialog');
    provideShortcutLayer(shortcutLayer);

    shortcutLayer.register('Escape', () => {
      // Consume even when isCloseOnEsc is off: the topmost overlay owns Escape.
      if (props.isCloseOnEsc) instance.proxy.close({ type: 'esc' });
    });

    // A consumer listening for @enter makes that the popper's default primary
    // action. Registered here, before slot content mounts, so a button-pair in
    // the content wins by last-registered order.
    if (instance.vnode.props?.onEnter) {
      shortcutLayer.registerPrimaryAction(() => emit('enter'));
    }

    return { shortcutLayer };
  },

  props: {
    isCloseOnEsc: { type: Boolean, default: false },
    animation: {
      // Built in animations: none, fade, slide-fade, fall-from-top-with-fade
      type: String,
      default: 'fall-from-top-with-fade',
    },
    arrow: {
      type: Boolean,
      default: false,
    },
    arrowClass: {
      type: [Array, Object, String],
      default: 'popper-arrow',
    },
    arrowWrapperClass: {
      type: [Array, Object, String],
      default: 'popper-arrow-wrapper',
    },
    arrowWrapperTag: {
      type: String,
      default: 'div', // Must NOT be a Vue component (but a DOM element tag)
    },
    beforeClose: {
      type: Function,
      default: () => {},
    },
    closeOnOutsideClick: {
      type: Boolean,
      default: true,
    },
    closeOnOutsideClickCheck: {
      type: Function,
    },
    defaultClass: {
      type: String,
      default: 'popper',
    },
    flip: {
      // Modifier quick-option - see Popper docs here https://popper.js.org/docs/v2/modifiers/flip/
      type: [Boolean, Number, Object, String], // Mix of flip.enabled (Boolean), flip.padding (Number | Object) and flip.rootBoundary (String)
      default: null,
    },
    modifiers: {
      type: Array,
    },
    offset: {
      // Modifier quick-option - see Popper docs here https://popper.js.org/docs/v2/modifiers/offset/
      type: [Array, Function],
    },
    onFirstUpdate: {
      type: Function,
    },
    show: {
      type: Boolean,
      default: false,
    },
    placement: {
      type: String,
      // Placements taken straight from: https://popper.js.org/docs/v2/constructors/#options
      validator: (placement) =>
        [
          'auto',
          'auto-start',
          'auto-end',
          'top',
          'top-start',
          'top-end',
          'bottom',
          'bottom-start',
          'bottom-end',
          'right',
          'right-start',
          'right-end',
          'left',
          'left-start',
          'left-end',
        ].includes(placement),
      default: 'bottom',
    },
    preventOverflowAltAxis: {
      // Modifier quick-option - see Popper docs here https://popper.js.org/docs/v2/modifiers/prevent-overflow/
      type: [Boolean],
    },
    preventOverflowMainAxis: {
      // Modifier quick-option - see Popper docs here https://popper.js.org/docs/v2/modifiers/prevent-overflow/
      type: [Boolean],
      default: true,
    },
    preventOverflowPadding: {
      // Modifier quick-option - see Popper docs here https://popper.js.org/docs/v2/modifiers/prevent-overflow/
      type: [Number, Object],
    },
    preventOverflowRootBoundary: {
      // Modifier quick-option - see Popper docs here https://popper.js.org/docs/v2/modifiers/prevent-overflow/
      type: [String],
    },
    preventOverflowTether: {
      // Modifier quick-option - see Popper docs here https://popper.js.org/docs/v2/modifiers/prevent-overflow/
      type: [Boolean],
      default: true,
    },
    reference: {
      type: [Function, Object, String],
    },
    // A selector, or the element itself where the caller knows which one it means — a
    // selector can only ever resolve to the first match in the document.
    selector: {
      type: [String, Object],
      default: '#portal-target',
    },
    // See Popper docs for options: https://popper.js.org/docs/v2/constructors/#options
    strategy: {
      type: String,
      // Strategies taken straight from: https://popper.js.org/docs/v2/constructors/#options
      validator: (strategy) => ['absolute', 'fixed'].includes(strategy),
      default: 'absolute',
    },
    tag: {
      type: String,
      default: 'div',
    },
    updateOnResize: {
      type: Boolean,
      default: true,
    },
    wrapperClass: {
      type: [Array, Object, String],
      default: 'popper-wrapper',
    },
    wrapperTag: {
      type: String,
      default: 'div', // Must NOT be a Vue component (but a DOM element tag)
    },
    noStyling: {
      type: Boolean,
      default: false,
    },
    shadowStyle: {
      type: String,
      default: 'modal',
      validator: (strategy) => ['down-slim', 'modal'].includes(strategy),
    },
  },

  directives: {
    resize: resizeDetection,
  },

  data() {
    return {
      currentTarget: null,
      triggerFocus: null,
      isOpen: false,
      isWrapperOpen: false,
      pointerDownElement: null,
      popperInstance: null,
      alreadyHandledClickOutside: false,

      // teleports are tricky with SSR, only render when mounting
      hasMounted: false,

      currentPlacement: null,

      // Nested poppers (registered via the $parent walk) — used only so
      // outside-click detection treats clicks inside a child popper's
      // teleported DOM as "inside". Keyboard priority is the shortcut-layer
      // stack in @lib/shortcuts, not this list.
      popperChildren: [],
    };
  },

  mounted() {
    this.hasMounted = true;
  },

  computed: {
    // Workaround for passing scopeId to div.popper (it's not technically the root, but to allow for parent to style with scoped styles - as is expected - it makes sense to add it)
    parentScopeId() {
      const $renderingComponent = this.$slots?.default()[0];

      return $renderingComponent?.scopeId || null;
    },
    popperOptions() {
      const popperOptions = {};
      const { modifiers, onFirstUpdate, placement, strategy } = this;

      popperOptions.modifiers = modifiers || [];
      if (onFirstUpdate) popperOptions.onFirstUpdate = onFirstUpdate;
      if (placement) popperOptions.placement = placement;
      if (strategy) popperOptions.strategy = strategy;

      // Modifier quick-option 'flip'
      if (!isNull(this.flip)) {
        if (typeof this.flip === 'boolean') {
          popperOptions.modifiers.push({
            name: 'flip',
            enabled: this.flip,
          });
        } else {
          let options;

          if (typeof this.flip === 'string') {
            options = { rootBoundary: this.flip };
          } else if (typeof this.flip === 'number') {
            options = { padding: this.flip };
          } else {
            // Object — full options pass-through (e.g. { rootBoundary, fallbackPlacements, padding })
            options = this.flip;
          }

          popperOptions.modifiers.push({
            name: 'flip',
            options,
          });
        }
      }

      // Modifier quick-option 'offset'
      if (this.offset) {
        popperOptions.modifiers.push({
          name: 'offset',
          options: {
            offset: this.offset,
          },
        });
      }

      // Modifier quick-option 'preventOverflow'
      if (
        this.preventOverflowAltAxis ||
        !this.preventOverflowMainAxis ||
        this.preventOverflowPadding ||
        this.preventOverflowRootBoundary ||
        !this.preventOverflowTether
      ) {
        const options = {};

        if (this.preventOverflowAltAxis) options.altAxis = this.preventOverflowAltAxis;
        if (!this.preventOverflowMainAxis) options.mainAxis = this.preventOverflowMainAxis;
        if (this.preventOverflowPadding) options.padding = this.preventOverflowPadding;
        if (this.preventOverflowRootBoundary) options.rootBoundary = this.preventOverflowRootBoundary;
        if (!this.preventOverflowTether) options.tether = this.preventOverflowTether;

        popperOptions.modifiers.push({
          name: 'preventOverflow',
          options,
        });
      }

      popperOptions.modifiers.push({
        name: 'popperListener',
        enabled: true,
        phase: 'main',
        fn: this.popperListener,
      });

      return popperOptions;
    },
    popperReference() {
      if (this.currentTarget) return this.currentTarget;

      if (!this.reference) {
        console.warn('FsPopper: No reference for popper found!', this);
        return;
      }

      if (isFunction(this.reference)) {
        return this.reference();
      }

      return this.reference;
    },
  },

  methods: {
    popperListener({ state }) {
      if (this.currentPlacement !== state.placement) {
        this.currentPlacement = state.placement;
        this.$emit('placement-changed', state.placement);
      }
    },
    enter() {
      this.shortcutLayer.activate();

      this.generatePopper();

      if (this.closeOnOutsideClick) {
        this.bindCloseTimeout = setTimeout(() => {
          // To avoid having it trigger immediately (on the click/event that triggered the popover)
          this.bindCloseOnOutsideClick();
        });
      }

      this.$emit('open');
    },
    beforeLeave() {
      this.shortcutLayer.deactivate();
      this.unbindCloseOnOutsideClick();
    },
    afterLeave() {
      this.isWrapperOpen = this.isOpen;
      this.$emit('fully-closed');
      this.destroyPopper();
    },
    bindCloseOnOutsideClick() {
      // Needs to be triggered before other events, since other events may change the DOM,
      // incorrectly break the parent chain (and thus fail the clickWithin test) and cause
      // a close to happen even though it shouldn't.
      //
      // If one needs to handle this in a different fashion, it's best to add beforeClose
      // check, or disable close-on-outside-click altogether.
      document.addEventListener('click', this.wrappedDocumentClick, { capture: true });
      document.addEventListener('pointerdown', this.wrappedPointerDown);
    },
    unbindCloseOnOutsideClick() {
      clearTimeout(this.bindCloseTimeout);
      document.removeEventListener('click', this.wrappedDocumentClick);
      document.removeEventListener('pointerdown', this.wrappedPointerDown);
    },
    documentPointerDown(event) {
      this.pointerDownElement = event.target;
    },
    documentClick(event) {
      // We're in the middle of opening/closing - make it a no-op to avoid issues
      if (this.isOpen !== this.isWrapperOpen || !this.$refs.popper) return;

      // Use pointer down element if it exists (it should), to take into consideration
      // the element where the click started rather than where it ended (which is where
      // the event.target of a click event is set to).
      // Fallback also helps with browsers not supporting pointerdown (Safari <13)
      const $target = this.pointerDownElement || event.target;
      this.pointerDownElement = null; // Reset to avoid mistakenly re-using an old value

      function clickWithinHelper($target, withinElement) {
        return withinElement.contains($target) || withinElement === $target;
      }

      // Click inside popper
      if (clickWithinHelper($target, this.$refs.popper)) return;

      for (let i = 0; i < this.popperChildren.length; i++) {
        const popperChildComponent = this.popperChildren[i];

        if (clickWithinHelper($target, popperChildComponent.$refs.popper)) return;
      }

      // A click landed in a modal or on a modal backdrop. Modals, backdrops and this
      // popper's wrapper are all teleported to #portal-target, so document order reflects
      // stacking: a modal opened *on top of* this popper is inserted after it, while the
      // modal that merely *contains* this popper's trigger is inserted before it.
      // Only keep the popper open for a modal stacked above it — otherwise clicking
      // elsewhere inside the popper's own modal would never dismiss the popper.
      const clickedModal = $target.closest('.modal-wrap, .backdrop');
      if (clickedModal) {
        const modalIsAbovePopper = Boolean(
          clickedModal.compareDocumentPosition(this.$refs.popper) & Node.DOCUMENT_POSITION_PRECEDING
        );
        if (modalIsAbovePopper) return;
      }

      // When dev, don't close poppers on clicks for vue-devtools
      if (insideVueDevtools($target)) {
        return;
      }

      // User extra check (useful for non-popper sub-components outside the popper DOM structure)
      if (this.closeOnOutsideClickCheck) {
        if (this.closeOnOutsideClickCheck($target, event)) return;
      }

      if (this.alreadyHandledClickOutside) return;

      // Ensure we don't trigger multiple close events if multiple clicks are registered before popper starts closing
      this.alreadyHandledClickOutside = true;

      // Emit early close so that we can trigger any events that should happen when popper closes but before we handle any
      // events triggered by the click that closed the popper
      this.$emit('early-close');

      // Otherwise, trigger close
      // Delay close in order to capture other events first (that might trigger toggling, and thus triggering a close, open again scenario)
      requestAnimationFrame(() => {
        this.alreadyHandledClickOutside = false;
        this.close({ type: 'click-outside' });
      });
    },
    open(event) {
      // currentTarget is what we want - but Safari 9 and below doesn't support it, thus fallback on target
      if (event?.currentTarget || event?.target) {
        this.currentTarget = event.currentTarget || event.target;
      }

      // Only on the way in: several callers re-open an already-open popper (a search field
      // opening on every keystroke, for one), and those calls say nothing about how the user
      // got here.
      if (!this.isOpen) this.captureTriggerFocus();

      this.isOpen = true;
    },
    async close(args) {
      // If click-outside, we already handled in 'documentClick'
      if (args?.type !== 'click-outside') {
        this.$emit('early-close');
      }

      // Break if promise returns true (truthy)
      if (await Promise.resolve(this.beforeClose(args))) return;

      this.isOpen = false;

      if (args?.type === 'esc') this.releaseTriggerFocus();

      // Cleanup
      this.currentTarget = null;
      this.triggerFocus = null;

      this.$emit('close');
    },
    /**
     * How the user got here, read off the browser's own modality tracking rather than off the
     * argument: a trigger the keyboard reached is already showing its focus ring by the time the
     * popper opens, a clicked one is not. Many callers pass { target } rather than a native event,
     * and this answers for those too.
     *
     * What gets remembered is the focused element itself, not the caller's target. The target is
     * often an inner element whose nearest focusable ancestor took the focus instead (fs-select
     * passes its .select-value while focus sits on the outer control), and poppers anchored
     * through the reference prop pass no target at all.
     */
    captureTriggerFocus() {
      const focused = document.activeElement;
      const openedByPointer =
        focused instanceof HTMLElement && focused !== document.body && !focused.matches(':focus-visible');

      this.triggerFocus = openedByPointer ? focused : null;
    },
    /**
     * Escape is a keypress, so the browser switches to keyboard modality and starts drawing
     * :focus-visible on whatever is focused. A trigger the user only ever clicked still holds
     * focus — nothing here moves it — so it is left ringed with no popper to explain the ring.
     * Dropping focus is what a click already implies; a trigger the keyboard opened keeps both
     * its focus and its ring, which is where Escape should put a keyboard user back.
     *
     * Poppers that focus something of their own (the tariff and colour editors, most of the
     * forms) never reach this: focus has moved on, so the trigger is no longer the active
     * element and is left alone.
     */
    releaseTriggerFocus() {
      if (this.triggerFocus && this.triggerFocus === document.activeElement) this.triggerFocus.blur();
    },
    // Returns close's promise so a rejecting beforeClose (which is where some poppers do their
    // save) reaches the app's error handler instead of becoming an unhandled rejection.
    toggle(event) {
      if (this.isOpen) {
        return this.close({ type: 'toggle', event });
      } else {
        this.open(event);
      }
    },
    generatePopper() {
      if (this.popperInstance) {
        console.warn('FsPopper: Trying to open an already open popper');
        return;
      }

      this.popperInstance = createPopper(this.popperReference, this.$refs.popper, this.popperOptions);
    },
    destroyPopper() {
      if (!this.popperInstance) return;

      this.popperInstance.destroy();
      this.popperInstance = null;
    },
    resize() {
      if (!this.popperInstance || !this.updateOnResize) return;

      this.popperInstance.update();
    },
    registerPopperChild(childPopperInstance) {
      this.popperChildren.push(childPopperInstance);
    },
    registerToPopperParent() {
      // recursively search parent nodes to find FsPopperInstance
      let parent = this.$parent;
      while (parent) {
        if (parent.FsPopperInstance === true) {
          parent.registerPopperChild(this);
        }
        parent = parent.$parent;
      }
    },
    unregisterFromPopperParent() {
      // recursively search parent nodes to find FsPopperInstance
      let parent = this.$parent;
      while (parent) {
        if (parent.FsPopperInstance === true) {
          parent.popperChildren = parent.popperChildren.filter((child) => child !== this);
        }
        parent = parent.$parent;
      }
    },
  },
  watch: {
    closeOnOutsideClick(val, oldVal) {
      if (Boolean(val) === Boolean(oldVal)) return;

      if (val) {
        this.bindCloseOnOutsideClick();
      } else {
        this.unbindCloseOnOutsideClick();
      }
    },
    show(val) {
      if (val !== this.isOpen) this.toggle();
    },
    isOpen(val) {
      if (val) this.isWrapperOpen = true;

      this.$emit('update:isOpen', val);
    },
    isWrapperOpen(val) {
      if (val) {
        this.registerToPopperParent();
      } else {
        this.unregisterFromPopperParent();
      }
    },
  },
  created() {
    this.FsPopperInstance = true; // Used to identify the component as a FsPopper instance
    this.wrappedDocumentClick = this.documentClick.bind(this);
    this.wrappedPointerDown = this.documentPointerDown.bind(this);
    this.throttledResize = rafThrottle(this.resize);
  },
  unmounted() {
    // If something removes the popper right away (e.g. parent disappears),
    // we still want to ensure proper cleanup. The transition leave hooks don't
    // run when the popper unmounts while open.
    this.shortcutLayer.destroy();
    this.unregisterFromPopperParent();
    this.unbindCloseOnOutsideClick();
    this.destroyPopper();
  },
};

export default FsPopper;
</script>

<style scoped lang="scss">
@import '@styles/variables';

// Animations
// ==========

// Fade
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

// Slide
.slide-fade-enter-active {
  transition:
    top 0.4s ease,
    opacity 0.4s ease;
}
.slide-fade-leave-active {
  transition:
    top 0.4s cubic-bezier(1, 0.5, 0.8, 1),
    opacity 0.4s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter,
.slide-fade-leave-to {
  top: -1rem;
  opacity: 0;
}

// Slide fade fast
.slide-fade-fast-enter-active {
  transition:
    top 0.12s ease,
    opacity 0.12s ease;
}
.slide-fade-fast-leave-active {
  transition:
    top 0.12s cubic-bezier(1, 0.5, 0.8, 1),
    opacity 0.12s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-fast-enter,
.slide-fade-fast-leave-to {
  top: -1rem;
  opacity: 0;
}

//.slide-fade-2-enter {transform: translateX(-100%);}
//.slide-fade-2-enter-active {transition: transform .15s;}
//.slide-fade-2-enter-to {transform: translateX(0);}
//
//.slide-fade-2-leave {transform: translateX(0);}
//.slide-fade-2-leave-active {transition: transform .15s;}
//.slide-fade-2-leave-to {transform: translateX(-100%);}

// ==========================================================================
// ==========================================================================
// Popper styles
.popper {
  border-radius: 1rem;
  background: #fff;
  box-shadow: 4px 8px 2rem rgba($c-dark, 0.4);

  &--down-slim-shadow {
    box-shadow: 0px 6px 1rem rgba($c-dark, 0.2);
  }

  &--empty-styling {
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }
}

/* Fixes a render issue in Safari, when having absolute
positioned content within a popper,
and a Vue update triggered in the mount function.  */
.popper-wrapper {
  position: absolute;
}
</style>
